import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../config/api';
import LoadingSpinner from '../components/LoadingSpinner';
import BannerDisplay from '../components/BannerDisplay';
import AuctionCard from '../components/AuctionCard';
import { BigCountdown } from '../components/CountdownTimer';

const HomePage = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [attualita, setAttualita] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nextAuction, setNextAuction] = useState(null);
  const [activeAuctionCount, setActiveAuctionCount] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [auctionsRes, attualitaRes, annRes] = await Promise.all([
          axios.get(`${API}/auctions`),
          axios.get(`${API}/attualita?limit=4`),
          axios.get(`${API}/announcements`)
        ]);
        
        const auctions = auctionsRes.data;
        
        // Find active auctions with future end_time
        const activeAuctions = auctions
          .filter(a => a.status === 'active' && new Date(a.end_time) > new Date())
          .sort((a, b) => new Date(a.end_time) - new Date(b.end_time));
        
        setActiveAuctionCount(activeAuctions.length);
        
        if (activeAuctions.length > 0) {
          setNextAuction(activeAuctions[0]);
        }
        
        setAttualita(attualitaRes.data);
        setAnnouncements(annRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      {/* Hero Section */}
      <section className="relative h-[60vh] md:h-[80vh] min-h-[400px] md:min-h-[600px] flex items-center" style={{
        backgroundImage: 'url(/hero-bg.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}>
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="container-custom relative z-10 px-4">
          <div className="max-w-2xl">
            <h1 className="font-display text-3xl sm:text-5xl md:text-7xl text-white mb-2 md:mb-3 leading-tight drop-shadow-lg">
              Equus Prime.<br />Il futuro delle aste equine.
            </h1>
            <p className="text-white/90 text-sm sm:text-base md:text-lg mb-4 md:mb-6 drop-shadow-md">
              Emozioni, tradizione, eccellenza.
            </p>
            <div className="flex flex-wrap gap-3 md:gap-4">
              <a href="/come-vendere" className="inline-block bg-[#c9a227] text-[#0a1628] font-bold uppercase tracking-wider text-[10px] px-3 py-1.5 rounded-full md:text-sm md:px-6 md:py-2.5 md:rounded-full hover:bg-[#b8911f] transition-all shadow-md" data-testid="hero-cta-sell">
                Vendi il tuo Cavallo
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Banner */}
      <div className="container-custom px-4">
        <BannerDisplay position="header" />
      </div>

      {/* Prossima Asta Section - Stile Travera */}
      {nextAuction && (
        <section className="-mt-12 md:-mt-20 relative z-20 pb-6 md:pb-8">
          <div className="container-custom px-4">
            <div className="bg-white border border-gray-200 shadow-lg rounded-2xl overflow-hidden">
              <div className="p-4 md:p-8">
                <p className="font-display text-xl md:text-2xl font-bold mb-6" data-testid="prossima-asta-title">PROSSIMA ASTA</p>
                
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-6">
                  <div>
                    <h2 className="text-base md:text-lg mb-3" style={{ fontFamily: 'Arial, sans-serif', fontWeight: 900, color: '#000' }}>
                      {(() => {
                        const days = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
                        const day = days[new Date(nextAuction.end_time).getDay()];
                        return `Asta del ${day}`;
                      })()}
                    </h2>
                    <div className="flex flex-wrap gap-3 md:gap-6 text-xs md:text-sm text-[#0a1628]">
                      <span>
                        Data : <strong>{new Date(nextAuction.end_time).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>
                      </span>
                      <span>
                        Prima chiusura : <strong>{new Date(nextAuction.end_time).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</strong>
                      </span>
                      <span>
                        Cavalli in vendita : <strong>{activeAuctionCount}</strong>
                      </span>
                    </div>
                  </div>
                  
                  <BigCountdown endTime={nextAuction.end_time} />
                </div>
              </div>
              
              <a 
                href="/aste" 
                className="block w-full bg-[#0a7e8c] text-white text-center py-3 md:py-4 font-medium hover:bg-[#086a76] transition-colors text-sm md:text-base"
                data-testid="vai-asta-btn"
              >
                Vai all'asta
              </a>
            </div>
          </div>
        </section>
      )}

      {/* Prossime Aste - Stile Travera (annunci gestiti dall'admin) */}
      <section className="pb-8 md:pb-12">
        <div className="container-custom px-4">
          <div className="bg-white border border-gray-200 shadow-sm rounded-2xl overflow-hidden">
            {/* Prossime Aste Header */}
            {announcements.length > 0 && (
              <div className="p-4 md:p-8">
                <p className="font-display text-xl md:text-2xl font-bold mb-6" data-testid="announcements-title">PROSSIME ASTE</p>
                <div className="bg-gray-50 rounded-xl p-4 md:p-6">
                  <div className="divide-y divide-gray-200">
                    {announcements.map(ann => (
                      <div key={ann.announcement_id} className="py-4 first:pt-0 last:pb-0" data-testid={`announcement-${ann.announcement_id}`}>
                        <p className="text-[#0a1628] text-sm md:text-base">{ann.text}</p>
                        <p className="font-bold text-sm md:text-base">{ann.time_text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Desideri vendere section */}
            <div className="bg-gray-50 p-4 md:p-8">
              <h3 className="font-display text-xl md:text-2xl font-bold mb-4">Desideri vendere il tuo cavallo a Equus Prime?</h3>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2 text-sm md:text-base text-[#0a1628]">
                  <svg className="w-5 h-5 text-[#c9a227] mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Assistenza durante tutta la transazione
                </li>
                <li className="flex items-start gap-2 text-sm md:text-base text-[#0a1628]">
                  <svg className="w-5 h-5 text-[#c9a227] mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Conoscenza, contatti ed esperienza
                </li>
                <li className="flex items-start gap-2 text-sm md:text-base text-[#0a1628]">
                  <svg className="w-5 h-5 text-[#c9a227] mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Sempre con il cliente al centro dell'attenzione.
                </li>
              </ul>
              <div className="flex flex-wrap gap-3">
                <a href="/come-vendere" className="btn-primary text-sm" data-testid="btn-learn-more">Per saperne di pi&ugrave;</a>
                <a href="/contatti" className="px-6 py-3 border-2 border-[#c9a227] text-[#c9a227] font-medium hover:bg-[#c9a227] hover:text-white transition-colors text-sm rounded" data-testid="btn-contact">Contattaci</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Risultati dell'asta - Mostra l'ultimo articolo (come Travera) */}
      {(() => {
        const latestArticle = attualita.length > 0 ? attualita[0] : null;
        if (!latestArticle) return null;

        return (
          <section className="pb-8 md:pb-12">
            <div className="container-custom px-4">
              <div className="bg-white border border-gray-200 shadow-sm rounded-2xl overflow-hidden">
                <div className="p-4 md:p-8 border-b border-gray-200">
                  <p className="font-display text-xl md:text-2xl font-bold" data-testid="results-title">RISULTATI DELL'ASTA</p>
                </div>
                
                <a 
                  href={latestArticle.link}
                  className="flex items-center gap-4 md:gap-6 p-4 md:p-6 hover:bg-gray-50 transition-colors"
                  data-testid="result-latest"
                >
                  {latestArticle.image_url && (
                    <img src={latestArticle.image_url} alt="" className="w-20 h-20 md:w-28 md:h-28 object-cover rounded flex-shrink-0" />
                  )}
                  <div>
                    <p className="text-[#64748b] text-xs md:text-sm">
                      {latestArticle.date ? new Date(latestArticle.date).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}
                    </p>
                    <h3 className="font-display text-base md:text-xl font-bold mt-1">
                      {latestArticle.title}
                    </h3>
                  </div>
                </a>
                
                <div className="bg-gray-50 p-4 text-center">
                  <a href="/risultati" className="text-[#0a7e8c] hover:underline font-medium text-sm md:text-base" data-testid="all-results-link">
                    Tutti i risultati dell'asta
                  </a>
                </div>
              </div>
            </div>
          </section>
        );
      })()}

      {/* Attualità - Feed unificato (articoli risultati + notizie) */}
      {attualita.length > 0 && (
        <section className="py-12 md:py-16">
          <div className="container-custom px-4">
            <h2 className="font-display text-2xl md:text-3xl text-center mb-8 md:mb-12" data-testid="news-section-title">Attualit&agrave;</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {attualita.slice(0, 4).map(item => (
                <a key={`${item.type}-${item.id}`} href={item.link} className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-testid={`attualita-card-${item.id}`}>
                  {item.image_url ? (
                    <div className="overflow-hidden rounded-xl m-3 mb-0">
                      <img 
                        src={item.image_url} 
                        alt={item.title}
                        className="w-full h-48 md:h-56 object-cover rounded-xl transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                  ) : (
                    <div className="w-full h-48 md:h-56 bg-gray-200 flex items-center justify-center">
                      <span className="text-gray-400 text-sm">Nessuna foto</span>
                    </div>
                  )}
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <p className="text-[#64748b] text-xs md:text-sm">
                        {item.date ? new Date(item.date).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}
                      </p>
                      {item.type === 'result_article' && (
                        <span className="text-[10px] bg-[#0a1628] text-white px-1.5 py-0.5 rounded">Risultati</span>
                      )}
                    </div>
                    <h3 className="font-display text-base md:text-lg font-bold group-hover:text-[#c9a227] transition-colors leading-tight">
                      {item.title}
                    </h3>
                    <p className="text-[#64748b] text-sm mt-2 line-clamp-3">{item.excerpt}</p>
                  </div>
                </a>
              ))}
            </div>
            <div className="text-center mt-8 md:mt-12">
              <a href="/attualita" className="inline-block bg-[#0a8a8a] text-white px-8 py-3 font-medium hover:bg-[#087878] transition-colors rounded" data-testid="all-news-btn">
                Mostra tutte le notizie
              </a>
            </div>
          </div>
        </section>
      )}

      {/* How It Works */}
      <section className="section bg-[#0a1628]">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="text-[#c9a227] uppercase tracking-widest text-sm font-bold mb-2">Semplice e sicuro</p>
            <h2 className="font-display text-4xl text-white">Come Funziona</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#c9a227] rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-[#0a1628] text-2xl font-bold">1</span>
              </div>
              <h3 className="font-display text-xl text-white mb-4">Registrati</h3>
              <p className="text-white/60">
                Crea il tuo account e invia i documenti di verifica per partecipare alle aste.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#c9a227] rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-[#0a1628] text-2xl font-bold">2</span>
              </div>
              <h3 className="font-display text-xl text-white mb-4">Fai un'Offerta</h3>
              <p className="text-white/60">
                Scegli il cavallo che ti interessa e fai la tua offerta. Minimo €500, incrementi da €100.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#c9a227] rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-[#0a1628] text-2xl font-bold">3</span>
              </div>
              <h3 className="font-display text-xl text-white mb-4">Vinci l'Asta</h3>
              <p className="text-white/60">
                Se la tua offerta è la più alta, ti contatteremo per finalizzare l'acquisto.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-[#102a43]">
        <div className="container-custom text-center">
          <h2 className="font-display text-4xl text-white mb-6">Vuoi vendere il tuo cavallo?</h2>
          <p className="text-white/70 text-lg mb-8 max-w-2xl mx-auto">
            Affidati a Equus Prime per raggiungere acquirenti qualificati in tutta Italia. 
            Ti guidiamo in ogni fase del processo.
          </p>
          <a href="/come-vendere" className="btn-primary">
            Scopri Come Vendere
          </a>
        </div>
      </section>
    </div>
  );
};


export default HomePage;
